import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, CardContent, Chip, Stack, Typography,
} from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { fetchRequest as fetchPaths, clearState as clearPaths } from '../../redux/actions/discovery/paths';
import PathDrawer from './PathDrawer';
import { useDisplayNames } from '../../utils/displayNames';

/**
 * Multi-hop pathways under the graph (spec §6.4, M13). Each chain is a
 * clickable row — click opens the PathDrawer with per-hop evidence and the
 * mediation narrative. Renders nothing when the goal has no pathways
 * (depth-1 workspaces stay clean).
 */
const PathwaysPanel = ({ goalId, goal }) => {
  const dispatch = useDispatch();
  const nameOf = useDisplayNames();
  const [selected, setSelected] = useState(null);
  const paths = useSelector((s) => s.discovery_paths?.response?.paths) || [];

  useEffect(() => {
    if (goalId) {
      dispatch(fetchPaths({ goalId }));
    }
    return () => { dispatch(clearPaths()); };
  }, [goalId, dispatch]);

  if (!paths.length) return null;

  return (
    <>
      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <RouteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">
              Pathways — chained influences ({paths.length})
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            {paths.map((path) => (
              <Stack
                key={path.path_id}
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={() => setSelected(path)}
                sx={{
                  px: 1.25, py: 0.75, borderRadius: 1, cursor: 'pointer',
                  border: '1px solid', borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
                  {[...path.hops.map((h) => h.source_key), goal?.target_key].map((key, i, arr) => (
                    <React.Fragment key={`${path.path_id}-${key}-${String(i)}`}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: i === arr.length - 1 ? 700 : 500, maxWidth: 140 }}
                        title={key}
                      >
                        {nameOf(key)}
                      </Typography>
                      {i < arr.length - 1 && (
                        <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
                {path.mediation && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={path.mediation === 'full' ? 'info' : 'warning'}
                    label={path.mediation === 'full' ? 'through' : 'direct + through'}
                  />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`lead ${path.total_lag_bins} bins`}
                />
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <PathDrawer
        path={selected}
        goal={goal}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

export default PathwaysPanel;
